'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackLeadFormConversion } from '@/lib/analytics';

interface LeadCaptureSectionProps {
  className?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  destination?: string;
}

const LeadCaptureSection = ({ className = '' }: LeadCaptureSectionProps) => {
  const router = useRouter();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    travelDate: '',
    passengers: '1',
    budget: '',
    travelType: 'flight',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!/^[\d\s\+\-\(\)]{7,15}$/.test(value.replace(/\s/g, ''))) return 'Please enter a valid phone number';
        return '';
      case 'destination':
        if (!value.trim()) return 'Destination is required';
        return '';
      default:
        return '';
    }
  };

  const validateAll = (): FormErrors => {
    const newErrors: FormErrors = {};
    (['name', 'email', 'phone', 'destination'] as const).forEach(field => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    });
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (touched[name]) {
      const err = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: err || undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const saveLead = (source: string) => {
    const lead = {
      id: `lead-${Date.now()}`,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      whatsapp: formData.phone,
      message: formData.message || `Enquiry for ${formData.destination} on ${formData.travelDate} for ${formData.passengers} passengers. Budget: ${formData.budget}`,
      bookingDetails: {
        type: formData.travelType as 'flight' | 'hotel' | 'holiday',
        destination: formData.destination,
        date: formData.travelDate,
        passengers: parseInt(formData.passengers) || 1,
      },
      timestamp: new Date().toISOString(),
      status: 'new',
      bookingType: 'lead',
      source,
    };
    if (typeof window !== 'undefined') {
      const existing = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
      existing.unshift(lead);
      localStorage.setItem('bookingLeads', JSON.stringify(existing));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched: Record<string, boolean> = {};
    (['name', 'email', 'phone', 'destination'] as const).forEach(f => { allTouched[f] = true; });
    setTouched(allTouched);
    const newErrors = validateAll();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setSubmitting(true);
    saveLead('homepage-form');
    trackLeadFormConversion({
      form_location: 'homepage_lead_section',
      destination: formData.destination,
      travel_type: formData.travelType,
      passengers: parseInt(formData.passengers) || 1,
      budget: formData.budget,
      method: 'form',
    });
    await new Promise(r => setTimeout(r, 800));
    router.push('/quote-thankyou');
  };

  const sendWhatsApp = () => {
    saveLead('homepage-whatsapp');
    trackLeadFormConversion({
      form_location: 'homepage_lead_section',
      destination: formData.destination,
      travel_type: formData.travelType,
      method: 'whatsapp',
    });
    const msg = encodeURIComponent(
      `Hi Dial For Flight! I'd like a quote.\n\n` +
      `Name: ${formData.name || 'Not provided'}\n` +
      `Email: ${formData.email || 'Not provided'}\n` +
      `Phone: ${formData.phone || 'Not provided'}\n` +
      `Type: ${formData.travelType}\n` +
      `Destination: ${formData.destination || 'Not specified'}\n` +
      `Date: ${formData.travelDate || 'Flexible'}\n` +
      `Passengers: ${formData.passengers}\n` +
      `Budget: ${formData.budget || 'Flexible'}\n` +
      `Notes: ${formData.message || 'Please send me your best options!'}`
    );
    window.open(`https://wa.me/447700900000?text=${msg}`, '_blank');
  };

  const fieldClass = (field: keyof FormErrors) => {
    const base = 'w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors';
    if (touched[field] && errors[field]) return `${base} border-red-400 bg-red-50 focus:border-red-500`;
    if (touched[field] && !errors[field] && formData[field]) return `${base} border-green-400 bg-green-50 focus:border-green-500`;
    return `${base} border-border focus:border-primary`;
  };

  const quickOptions = [
    { icon: 'PaperAirplaneIcon', label: 'Cheap Flights', msg: "Hi! I\'m looking for cheap flights. Can you help me find the best deals?" },
    { icon: 'BuildingOfficeIcon', label: 'Hotel Deals', msg: "Hi! I\'m looking for hotel deals. Can you send me your best options?" },
    { icon: 'SunIcon', label: 'Holiday Packages', msg: "Hi! I'm interested in holiday packages. What deals do you have available?" },
    { icon: 'GlobeAltIcon', label: 'Group Travel', msg: "Hi! I\'m planning group travel. Can you provide group booking quotes?" },
  ];

  return (
    <section suppressHydrationWarning className={`py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-white to-accent/5 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/20 mb-4">
            <Icon name="BoltIcon" size={18} className="text-accent" />
            <span className="text-sm font-black text-accent">FREE QUOTES &bull; INSTANT RESPONSE</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-4">
            Get Your Free Travel Quote
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Fill in your details and our UK travel experts will send you the best deals within 1 hour &mdash; or chat instantly on WhatsApp!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-2xl border-2 border-accent/20 overflow-hidden">
              <div className="bg-gradient-to-r from-primary to-secondary p-6">
                <h3 className="text-white font-heading font-black text-xl">Send Us Your Enquiry</h3>
                <p className="text-white/80 text-sm mt-1">We respond within 1 hour during business hours</p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Full Name *</label>
                    <input
                      type="text" name="name" value={formData.name}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="John Smith"
                      className={fieldClass('name')}
                    />
                    {touched.name && errors.name && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{errors.name}
                      </p>
                    )}
                    {touched.name && !errors.name && formData.name && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Phone / WhatsApp *</label>
                    <input
                      type="tel" name="phone" value={formData.phone}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="+44 7700 900000"
                      className={fieldClass('phone')}
                    />
                    {touched.phone && errors.phone && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{errors.phone}
                      </p>
                    )}
                    {touched.phone && !errors.phone && formData.phone && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Email Address *</label>
                  <input
                    type="email" name="email" value={formData.email}
                    onChange={handleChange} onBlur={handleBlur}
                    placeholder="john@example.com"
                    className={fieldClass('email')}
                  />
                  {touched.email && errors.email && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={12} />{errors.email}
                    </p>
                  )}
                  {touched.email && !errors.email && formData.email && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={12} />Looks good!
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Travel Type</label>
                    <select name="travelType" value={formData.travelType} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                      <option value="flight">Flight</option>
                      <option value="hotel">Hotel</option>
                      <option value="holiday">Holiday Package</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Destination *</label>
                    <input
                      type="text" name="destination" value={formData.destination}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="Dubai, Spain, USA..."
                      className={fieldClass('destination')}
                    />
                    {touched.destination && errors.destination && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{errors.destination}
                      </p>
                    )}
                    {touched.destination && !errors.destination && formData.destination && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Travel Date</label>
                    <input type="date" name="travelDate" value={formData.travelDate} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Passengers</label>
                    <select name="passengers" value={formData.passengers} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                      {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Budget (GBP)</label>
                    <select name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                      <option value="">Any</option>
                      <option value="Under £500">Under £500</option>
                      <option value="£500-£1k">£500 - £1,000</option>
                      <option value="£1k-£2k">£1,000 - £2,000</option>
                      <option value="£2k-£5k">£2,000 - £5,000</option>
                      <option value="Over £5k">Over £5,000</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Additional Notes</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows={3} placeholder="Special requirements, preferred airlines, flexible dates..." className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors resize-none" />
                </div>
                {Object.keys(errors).length > 0 && Object.keys(touched).length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Icon name="ExclamationTriangleIcon" size={16} className="text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-600">Please fix the errors above before submitting.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={sendWhatsApp}
                    className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-4 rounded-xl font-bold hover:bg-[#20BA5A] transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-accent to-[#FF8C5A] text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 disabled:opacity-70"
                  >
                    {submitting ? (
                      <><Icon name="ArrowPathIcon" size={18} className="animate-spin" /> Sending...</>
                    ) : (
                      <><Icon name="PaperAirplaneIcon" size={18} /> Send Enquiry</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Side: Quick Options + Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick WhatsApp Options */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-[#25D366]/20 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#25D366] rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <div>
                  <h4 className="font-heading font-black text-foreground">Quick WhatsApp Enquiry</h4>
                  <p className="text-xs text-muted-foreground">Tap to send instantly</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickOptions.map((opt, i) => (
                  <a
                    key={i}
                    href={`https://wa.me/447700900000?text=${encodeURIComponent(opt.msg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 bg-[#25D366]/10 hover:bg-[#25D366]/20 rounded-xl transition-colors text-center group"
                  >
                    <Icon name={opt.icon as any} size={24} className="text-[#25D366] group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-bold text-foreground">{opt.label}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white">
              <h4 className="font-heading font-black text-lg mb-4">Contact Our UK Team</h4>
              <div className="space-y-3">
                <a href="tel:02046152876" className="flex items-center gap-3 hover:text-accent transition-colors">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="PhoneIcon" size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70">Call Us</p>
                    <p className="font-bold">02046152876</p>
                  </div>
                </a>
                <a href="mailto:info@dialforflight.co.uk" className="flex items-center gap-3 hover:text-accent transition-colors">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="EnvelopeIcon" size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70">Email Us</p>
                    <p className="font-bold">info@dialforflight.co.uk</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/447700900000?text=Hi%20I%27d%20like%20to%20enquire%20about%20a%20travel%20booking"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-accent transition-colors"
                >
                  <div className="w-10 h-10 bg-[#25D366]/80 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-white/70">WhatsApp</p>
                    <p className="font-bold">Chat Instantly</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
